import { env } from '@/env/client';
import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useEffect } from 'react';
import z from 'zod';

// Toggle to enable/disable auto-updates
// Disabled on development
const AUTO_UPDATE_ENABLED = import.meta.env.MODE !== 'development';

// If version is 'builtin', we treat it as 0, so ANY update will apply. If it's
// a timestamp string, we parse it.
const internalVersionSchema = z.union([
  z.literal('builtin').transform(() => 0),
  z.coerce.number().int().nonnegative(),
]);

const remoteVersionResponseSchema = z.object({
  version: z.coerce.number().int().nonnegative(),
});

const fetchRemoteVersion = async () => {
  const versionUrl = new URL('/version.json', env.VITE_SITE_URL);
  console.log('[AutoUpdate] Fetching version from:', versionUrl.toString());
  const response = await fetch(versionUrl, {
    cache: 'no-cache',
  });

  if (!response.ok) {
    throw new Error('Could not fetch remote version');
  }

  const responseJson = await response.json();
  const { version } = remoteVersionResponseSchema.parse(responseJson);

  return version;
};

const fetchRemoteBundle = async (remoteVersion: string) => {
  const bundleUrl = new URL('bundle.zip', env.VITE_SITE_URL);
  console.log('[AutoUpdate] Fetching bundle from:', bundleUrl.toString());
  return await CapacitorUpdater.download({
    url: bundleUrl.toString(),
    version: remoteVersion,
  });
};

export function useAutoUpdater() {
  useEffect(() => {
    const safeShowSplash = async () => {
      try {
        await SplashScreen.show({
          autoHide: false,
          fadeInDuration: 150,
        });
      } catch (error) {
        console.warn('[AutoUpdate] Failed to show splash screen', error);
      }
    };

    const safeHideSplash = async () => {
      try {
        await SplashScreen.hide();
      } catch (error) {
        console.warn('[AutoUpdate] Failed to hide splash screen', error);
      }
    };

    // Early return if auto-update is disabled
    if (!AUTO_UPDATE_ENABLED) {
      console.log('[AutoUpdate] Auto-update is disabled');
      // Ensure splash screen hides if updates are disabled
      if (Capacitor.isNativePlatform()) {
        void safeHideSplash();
      }
      return;
    }

    // Immediately notify Capgo on mount to prevent rollback loops
    CapacitorUpdater.notifyAppReady().catch((err) =>
      console.warn('[AutoUpdate] notifyAppReady failed', err),
    );

    const platform = Capacitor.getPlatform();
    console.log('[AutoUpdate] Hook mounted on platform:', platform);

    // Web check
    if (!Capacitor.isNativePlatform()) {
      console.log('[AutoUpdate] Skipping, not a native platform');
      return;
    }

    console.log('[AutoUpdate] Initializing auto-updater', env.VITE_SITE_URL);

    let isMounted = true;
    let isChecking = false;
    let resumeHandle: PluginListenerHandle | undefined;

    const checkAndApplyUpdate = async () => {
      // Fetch Remote Version
      try {
        // Keep the splash visible while update checks and downloads run.
        await safeShowSplash();

        // Fetch the remote version and validate that the response JSON is
        // valid.
        const remoteVersion = await fetchRemoteVersion();
        console.log('[AutoUpdate] Remote version:', remoteVersion);

        // Get the current internal bundle version
        let currentVersion = 0;
        try {
          const current = await CapacitorUpdater.current();
          console.log(
            '[AutoUpdate] Current Internal Bundle Version:',
            current.bundle.version,
          );
          if (current.bundle.version) {
            currentVersion = internalVersionSchema.parse(
              current.bundle.version,
            );
          }
        } catch (error) {
          console.warn(
            '[AutoUpdate] current() failed, assuming builtin',
            error,
          );
        }
        console.log('[AutoUpdate] Current version:', currentVersion);

        if (remoteVersion > currentVersion) {
          console.log('[AutoUpdate] New update found! Downloading...');
          const bundle = await fetchRemoteBundle(`${remoteVersion}`);
          console.log('[AutoUpdate] Download complete, setting version...');
          await CapacitorUpdater.set(bundle);
          console.log('[AutoUpdate] Update set. App will update on reload.');
        } else {
          console.log('[AutoUpdate] App is up to date.');
          // No update needed, allow user to see the app
          await safeHideSplash();
        }
      } catch (error) {
        console.error('[AutoUpdate] Failed', error);
        // On error, ensure we don't lock the user out
        await safeHideSplash();
      }
    };

    const runUpdateFlow = async () => {
      if (isChecking || !isMounted) return;
      isChecking = true;
      await checkAndApplyUpdate();
      isChecking = false;
    };

    // Run on mount
    void runUpdateFlow();

    // Run on Resume
    App.addListener('resume', runUpdateFlow).then((handle) => {
      if (isMounted) {
        resumeHandle = handle;
      } else {
        void handle.remove();
      }
    });

    return () => {
      isMounted = false;
      resumeHandle?.remove();
    };
  }, []);
}
